import { ChevronLeftIcon } from "@heroicons/react/outline";
import ErrorBanner from "components/lib/Shared/ErrorBanner";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import Tabs from "components/lib/Shared/Tabs";
import { Constants } from "config/constants";
import { ManageModuleContext } from "context/ManageCompanyModuleContext";
import UpdateHistoryContext from "context/UpdateHistoryContext";
import { Field, Form, Formik } from "formik";
import { useToast } from "hooks/useToast";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { getAllInitiateTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import { getRegisterOfApplicationsAndAllotmentsAsync } from "state/slices/minuteBook";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import ChangedDocuments from "./ChangedDocuments";
import MinuteBookEntryTable from "./MinuteBookEntryTable";

const Approvals = (props) => {
  const { approvalComponentsCallBack, transactions } = props;
  const { handleBackToManageYourCompanyDashboard } =
    useContext(ManageModuleContext);
  const uploadFileName = useSelector(
    (state) =>
      state.companySlice.validateCompanyUploadChangeDocuments.documentName
  );
  const pages = [
    { name: "Home", href: "/companies", current: false },
    {
      name: "Maintain a company",
      current: false,
      href: "#",
      handleClick: handleBackToManageYourCompanyDashboard,
    },
    {
      name: "Changes",
      current: true,
      href: null,
      handleClick: false,
    },
  ];
  const currentUser = useSelector((state) => state.userSlice);
  const params = useParams();
  const { toast } = useToast(4000);
  const { createdTransactionsIds, setCreatedTransactionsIds } =
    useContext(UpdateHistoryContext);

  const trxWithDirectorDocs = transactions.find(
    (trx) => trx?.approvalDocs?.directorApprovalDocs
  );
  const trxWithShareholderDocs = transactions.find(
    (trx) => trx?.approvalDocs?.directorApprovalDocs
  );
  const trxWithGazetteDocs = transactions.find(
    (trx) => trx?.approvalDocs?.directorApprovalDocs
  );
  const trxWithCreditorDocs = transactions.find(
    (trx) => trx?.approvalDocs?.directorApprovalDocs
  );
  const trxWithCourtOrderDocs = transactions.find(
    (trx) => trx?.approvalDocs?.directorApprovalDocs
  );

  const directorFile =
    trxWithDirectorDocs?.approvalDocs?.directorApprovalDocs?.templateFile
      ?.fileName ?? "";
  const shareholderFile =
    trxWithShareholderDocs?.approvalDocs?.shareholderApprovalDocs?.templateFile
      ?.fileName ?? "";
  const courtOrderFile =
    trxWithCourtOrderDocs?.approvalDocs?.courtOrderApprovalDocs?.templateFile
      ?.fileName ?? "";
  const gazetteFile =
    trxWithGazetteDocs?.approvalDocs?.gazetteApprovalDocs?.templateFile
      ?.fileName ?? "";
  const creditorFile =
    trxWithCreditorDocs?.approvalDocs?.creditorApprovalDocs?.templateFile
      ?.fileName ?? "";
  const approvalFiles = {
    directorFile,
    shareholderFile,
    courtOrderFile,
    gazetteFile,
    creditorFile,
  };

  let initialTabs = React.useMemo(() => {
    if (!transactions.length) return [];

    let tabs = [];

    // Default logic for non-CESSATION cases
    if (tabs.length === 0) {
      const conditions = [
        {
          key: "director_approval",
          label: "Director Approval",
          type: "boardApproval",
        },
        {
          key: "shareholder_approval",
          label: "Shareholder Approval",
          type: "shareholderApproval",
        },
        {
          key: "gazette_approval",
          label: "Gazette Notice",
          type: "gazetteNotice",
        },
        {
          key: "creditors_appointment",
          label: "Creditors Resolution",
          type: "creditorsResolution",
        },
        {
          key: "court_order_appointment",
          label: "Court Order",
          type: "courtOrder",
        },
      ];

      let filteredConditions = [];
      filteredConditions = conditions.filter(({ type }) =>
        transactions.some((transaction) => {
          if (
            transaction?.approvalType ===
            Constants.TRANSACTION_APPROVAL_TYPES.BOARD_SHAREHOLDER_APPROVAL
          ) {
            if (type === "boardApproval" || type === "shareholderApproval") {
              return true;
            }
          } else if (
            transaction?.approvalType ===
            Constants.TRANSACTION_APPROVAL_TYPES.BOARD_CREDITORS_APPROVAL
          ) {
            if (type === "boardApproval" || type === "creditorsResolution") {
              return true;
            }
          } else if (
            transaction?.approvalType ===
            Constants.TRANSACTION_APPROVAL_TYPES.BOARD_COURT_APPROVAL
          ) {
            if (type === "boardApproval" || type === "courtOrder") {
              return true;
            }
          } else {
            return (
              !transaction.approvalType || transaction.approvalType === type
            );
          }
        })
      );

      tabs = filteredConditions.map(({ key, label }) => ({
        id: key,
        label,
      }));
    }

    // Check if any transaction should show Minute Book Entry
    const hasMinuteBookTransaction = transactions.some((transaction) => {
      const processTypesToCheck = [
        "privateWithMoreThanOneClass",
        "unallotedShares",
        "PVTwithOneClass",
        "existingAuth",
        "shareTransfer",
      ];
      return processTypesToCheck.includes(transaction.processType);
    });

    // Add Minute Book Entry tab first if applicable
    if (hasMinuteBookTransaction) {
      tabs.unshift({
        id: "minute_book_entry",
        label: "Minute Book Entry",
      });
    }

    return tabs;
  }, [transactions]);

  const dispatch = useDispatch();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const isFirstStep = selectedTabIndex === 0;
  const isLastStep = selectedTabIndex === initialTabs.length - 1;
  const [submitting, setSubmitting] = useState(false);
  const [initialValues, setInitialValues] = useState({
    role: [],
    changeDocumentsType: "",
    director_approval: "",
    shareholder_approval: "",
    creditors_appointment: "",
    consentResolutionType: "",
    directorResolution: "",
    shareholderResolution: "",
    court_order_appointment: "",
    gazette_approval: "",
    consentResolutionTypeFile: null,
    consentSflResolutionTypeFile: null,
  });
  const formStepErrors = useRef(null);
  const [hasErrors, setHasErrors] = useState(false); // State to track errors
  const trxIds = transactions.map((trxId) => trxId._id);
  const { companyId } = useParams();
  const history = useHistory();
  const storageKey = "approvalDocsValues";

  // Lift minute book state to parent component to persist across tab switches
  const [minuteBookRows, setMinuteBookRows] = useState([]);
  const [minuteBookOriginalRows, setMinuteBookOriginalRows] = useState([]);
  let approvalDocsValues = {};

  // Fetch register of applications and allotments data when component mounts
  useEffect(() => {
    if (companyId) {
      dispatch(
        getRegisterOfApplicationsAndAllotmentsAsync({
          queryParams: { companyId },
        })
      );
    }
  }, [companyId, dispatch]);

  /** Convert localstorage value to a File */
  if (JSON.parse(localStorage.getItem(storageKey))) {
    approvalDocsValues = JSON.parse(localStorage.getItem(storageKey));
    Object.entries(approvalDocsValues).forEach(([key, value]) => {
      const { name, type, lastModified, dataUrl } = approvalDocsValues[key];
      const base64 = dataUrl.split(",")[1];
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      approvalDocsValues[key] = new File([bytes], name, { type, lastModified });
    });
  }

  // Move useEffect calls to component level
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    if (formValues.fileUploads) {
      setHasErrors(false);
    }
  }, [formValues.fileUploads]);

  useEffect(() => {
    if (
      formValues?.fileUploads &&
      Object.values(formValues?.fileUploads).length > 0 &&
      !Object.values(formValues?.fileUploads).includes("")
    ) {
      const fileObj = {};
      Object.entries(formValues.fileUploads).forEach(([key, value]) => {
        if (formValues.fileUploads[key] instanceof File) {
          const reader = new FileReader();
          reader.onload = () => {
            fileObj[key] = {
              path: value?.path,
              name: value?.name,
              type: value?.type,
              dataUrl: reader.result,
            };
            localStorage.setItem(storageKey, JSON.stringify(fileObj));
          };
          reader.readAsDataURL(formValues.fileUploads[key]);
        }
      });
    }
  }, [formValues]);

  useEffect(() => {
    // Update initial values dynamically
    setInitialValues({
      ...initialValues,
      transactions,
    });
  }, [transactions]);

  useEffect(() => {
    let fileUploads = {};
    fileUploads = buildFileUploads(initialTabs, fileUploads, approvalFiles);
    setInitialValues({
      ...initialValues,
      fileUploads: {
        ...fileUploads,
        ...approvalDocsValues,
      },
      transactions,
    });
  }, [initialTabs]);

  const handleGoBack = () => {
    if (!isFirstStep) {
      setSelectedTabIndex((prev) => prev - 1);
    } else {
      props.handleGoBack();
    }
  };

  const handleSubmit = async (values, selectedTab) => {
    try {
      const errors = [];

      // Skip validation for minute book entry tab
      if (selectedTab.id !== "minute_book_entry") {
        values = buildValuesForErrorValidation(initialTabs, values);

        validateFormData(initialTabs, values, errors);

        // Update the error state
        if (errors.length) {
          formStepErrors.current = {
            ...formStepErrors.current,
            [initialTabs.id]: errors,
          };
          setHasErrors(true); // Set hasErrors to true
          return; // Stop further execution if there are errors
        }
      }

      // If no errors, proceed with form submission
      setHasErrors(false); // Reset hasErrors to false

      setSubmitting(true);
      if (selectedTab.id === "minute_book_entry") {
        props?.handleGoForward();

        setSubmitting(false);
        return;
      }
      let payload = {
        ...values,
        changeDocumentSignature: {
          required: !values.changeDocumentsType.documentsAreSigned,
        },
        companyId: params.companyId,
        uploadedBy: currentUser._id,
        status: transactions[0].status,
        transactionType: transactions[0]?.transactionType || "",
      };

      payload = buildPayloadFromValues(values, payload);

      await handleCompanyMaintenanceFormData(
        "director-approval",
        payload,
        params.companyId,
        transactions,
        uploadFileName,
        trxIds,
        selectedTab.id
      );

      localStorage.removeItem(storageKey);
      dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId,
          params: {
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            transactionIds: createdTransactionsIds.filter((id) => Boolean(id)),
          },
        })
      );
      toast("success", `The application has been updated successfully`);
      props?.handleGoForward();
    } catch (error) {
      toast("error", "An error occurred while submitting the form.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-1">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values) => {
          if (!isLastStep) {
            setSelectedTabIndex((prev) => prev + 1);
          }
          if (isLastStep) {
            handleSubmit(values, initialTabs[selectedTabIndex]);
          }
        }}
      >
        {({ values, setFieldValue, isSubmitting }) => {
          // Update formValues when values change (no useEffect needed)
          if (JSON.stringify(formValues) !== JSON.stringify(values)) {
            setFormValues(values);
          }

          return (
            <Form>
              {hasErrors && (
                <ErrorBanner
                  errors={formStepErrors.current?.[initialTabs.id]}
                  className="my-4"
                />
              )}
              <div className="relative py-2 mt-2">
                <h3 className="font-bold text-slate-800">
                  {initialTabs[selectedTabIndex]?.id === "minute_book_entry"
                    ? "Minute Book Entry"
                    : "Documents Upload"}
                </h3>
                <Tabs
                  tabs={initialTabs}
                  selectedTabIndex={selectedTabIndex}
                  setSelectedTabIndex={setSelectedTabIndex}
                />

                {/* Conditional rendering based on selected tab */}
                {initialTabs[selectedTabIndex]?.id === "minute_book_entry" ? (
                  <MinuteBookEntryTable
                    transactions={transactions}
                    rows={minuteBookRows}
                    setRows={setMinuteBookRows}
                    originalRows={minuteBookOriginalRows}
                    setOriginalRows={setMinuteBookOriginalRows}
                  />
                ) : (
                  <Field
                    name={
                      initialTabs[selectedTabIndex]?.id || "changeDocumentsType"
                    }
                    component={ChangedDocuments}
                    idType="Foreigner"
                    designation="Director"
                    uploadRequestType={initialTabs[selectedTabIndex]?.label}
                  />
                )}

                <div className="flex justify-between mt-4">
                  <div>
                    {
                      <button
                        type="button"
                        onClick={handleGoBack}
                        className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
                      >
                        <span className="flex items-center mr-4 h-7">
                          <ChevronLeftIcon
                            className="flex-shrink-0 w-5 h-5"
                            aria-hidden="true"
                          />
                        </span>
                        <p className="font-semibold">Go Back</p>
                      </button>
                    }
                  </div>
                  <button
                    className={`${
                      !isLastStep
                        ? "flex items-start float-right py-2 ml-auto font-semibold bg-sflPrimary text-white px-4 rounded mt-7 mb-4"
                        : "flex items-center px-4 py-2 mb-2 font-semibold text-white rounded bg-green-focused mt-7 disabled:cursor-not-allowed"
                    }`}
                  >
                    {submitting && isLastStep ? (
                      <>
                        <SpinnerIcon />
                        Submitting...
                      </>
                    ) : (
                      "Save and Proceed"
                    )}
                  </button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default Approvals;

function buildFileUploads(initialTabs, fileUploads, files) {
  for (const tab of initialTabs) {
    if (tab.id === "director_approval") {
      fileUploads = {
        ...fileUploads,
        ownConsentResolutionForDirectorApproval: files.directorFile,
      };
    }
    if (tab.id === "shareholder_approval") {
      fileUploads = {
        ...fileUploads,
        ownConsentResolutionForShareholderApproval: files.shareholderFile,
      };
    }
    if (tab.id === "gazette_approval") {
      fileUploads = {
        ...fileUploads,
        ownConsentResolutionForGazetteApproval: files.gazetteFile,
      };
    }
    if (tab.id === "creditors_appointment") {
      fileUploads = {
        ...fileUploads,
        ownConsentResolutionForCreditorsApproval: files.creditorFile,
      };
    }
    if (tab.id === "court_order_appointment") {
      fileUploads = {
        ...fileUploads,
        ownConsentResolutionForCourtOrderApproval: files.courtOrderFile,
      };
    }
  }
  return fileUploads;
}

function buildValuesForErrorValidation(initialTabs, values) {
  for (const tab of initialTabs) {
    if (tab.id === "director_approval") {
      values = {
        ...values,
        director_approval: {
          documentsAreSigned: "true",
        },
      };
    }
    if (tab.id === "shareholder_approval") {
      values = {
        ...values,
        shareholder_approval: {
          documentsAreSigned: "true",
        },
      };
    }
    if (tab.id === "gazette_approval") {
      values = {
        ...values,
        gazette_approval: {
          documentsAreSigned: "true",
        },
      };
    }
    if (tab.id === "creditors_appointment") {
      values = {
        ...values,
        creditors_appointment: {
          documentsAreSigned: "true",
        },
      };
    }
    if (tab.id === "court_order_appointment") {
      values = {
        ...values,
        court_order_appointment: {
          documentsAreSigned: "true",
        },
      };
    }
  }
  return values;
}

function buildPayloadFromValues(values, payload) {
  if (values.fileUploads?.ownConsentResolutionForDirectorApproval) {
    payload = {
      ...payload,
      director_approval: {
        documentsAreSigned: "true",
      },
    };
  }

  if (values.fileUploads?.ownConsentResolutionForShareholderApproval) {
    payload = {
      ...payload,
      shareholder_approval: {
        documentsAreSigned: "true",
      },
    };
  }

  if (values.fileUploads?.ownConsentResolutionForCourtOrderApproval) {
    payload = {
      ...payload,
      court_order_appointment: {
        documentsAreSigned: "true",
      },
    };
  }

  if (values.fileUploads?.ownConsentResolutionForGazetteApproval) {
    payload = {
      ...payload,
      gazette_approval: {
        documentsAreSigned: "true",
      },
    };
  }

  if (values.fileUploads?.ownConsentResolutionForCreditorsApproval) {
    payload = {
      ...payload,
      creditors_appointment: {
        documentsAreSigned: "true",
      },
    };
  }
  return payload;
}

function validateFormData(initialTabs, values, errors) {
  for (const tab of initialTabs) {
    // Skip validation for minute book entry tab
    if (tab.id === "minute_book_entry") {
      continue;
    }

    if (
      tab.id === "director_approval" &&
      ((values.director_approval.documentsAreSigned === "false" &&
        !values.directorResolution.directorResolution) ||
        (values.director_approval.documentsAreSigned === "true" &&
          !values.fileUploads.ownConsentResolutionForDirectorApproval))
    ) {
      errors.push("Director approval document is required.");
    } else if (
      tab.id === "shareholder_approval" &&
      ((values.shareholder_approval.documentsAreSigned === "false" &&
        !values.shareholderResolution.shareholderResolution) ||
        (values.shareholder_approval.documentsAreSigned === "true" &&
          !values.fileUploads.ownConsentResolutionForShareholderApproval))
    ) {
      errors.push("Shareholder approval document is required.");
    } else if (
      tab.id === "gazette_approval" &&
      !values.fileUploads?.ownConsentResolutionForGazetteApproval
    ) {
      errors.push("Gazette approval document is required.");
    } else if (
      tab.id === "court_order_appointment" &&
      !values.fileUploads?.ownConsentResolutionForCourtOrderApproval
    ) {
      errors.push("Court order appointment document is required.");
    } else if (
      tab.id === "creditors_appointment" &&
      !values.fileUploads?.ownConsentResolutionForCreditorsApproval
    ) {
      errors.push("Creditors appointment document is required.");
    }
  }
}
